import {
  TripSortCriteria,
  AssetStatus,
  GetCustomFieldDefinitionsQuery,
  useGetExtensionsQuery,
} from "../../../graphql/generated";
import { TableField, TableAction } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  DeleteMutation,
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
// import customFieldDefinitionSchema from "../CustomFieldDefinitionForm/customFieldDefinitionSchema";
// import customFieldDefinitionsMapping from "../../../utils/mappin-s/customFieldDefinitions";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip,
} from "@mui/material";
import { useState, useMemo } from "react";
import CustomFieldDefinitionFormContainer from "../CustomFieldDefinitionForm";
import { useRoles } from "../../account/Access/RoleGuard";
import { capitalize } from "lodash";
import customFieldContextLabel from "../../../utils/labels/customFieldContextLabel";
import { useTranslation } from "react-i18next";
import { Delete, Extension as ExtensionIcon } from "@mui/icons-material";
import useDialog from "../../../utils/hooks/useDialog";
import { useSnackbar } from "notistack";
import { testIds } from "./CustomFieldsList.testIds";

export type CustomFieldDefinitionListItem =
  GetCustomFieldDefinitionsQuery["cutomFieldDefinitions"]["data"][0];

export default function CustomFieldDefinitionsList({
  query,
  deleteMutation,
  onRefresh,
}: // onBulkCreate,
{
  query: QueryFn<
    CustomFieldDefinitionListItem,
    "cutomFieldDefinitions",
    TripSortCriteria
  >;
  deleteMutation: DeleteMutation;
  onRefresh: () => void;
  // onBulkCreate: (data: NewCustomFieldDefinitionInput[]) => Promise<any>;
}) {
  // const { t } = useTranslation("users");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCustomFieldDefinition, setSelectedCustomFieldDefinition] =
    useState<CustomFieldDefinitionListItem | null>(null);

  const roles = useRoles();

  const hasWriteAccess = roles.some((role) =>
    ["Carrier Admin", "Manager", "Dispatcher"].includes(role)
  );

  const { t } = useTranslation("settings");
  const { showDialog, hideDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch extensions to map extensionId to extension name
  const { data: extensionsData } = useGetExtensionsQuery({});

  // Create a map of extensionId to extension name
  const extensionMap = useMemo(() => {
    if (!extensionsData?.extensions?.data) return {};

    const map: Record<string, string> = {};
    extensionsData.extensions.data.forEach((ext) => {
      map[ext.extension._id] = ext.extension.name;
    });
    return map;
  }, [extensionsData]);

  // Get delete mutation handle
  const deleteMutationHandle = deleteMutation ? deleteMutation() : null;

  // Custom actions including conditional delete
  const customActions: TableAction<CustomFieldDefinitionListItem>[] =
    useMemo(() => {
      if (!hasWriteAccess || !deleteMutationHandle) return [];

      return [
        {
          icon: <Delete />,
          tooltip: t("customFields.delete", "Delete"),
          isApplicable: (record) => !record.isExtensionField,
          onClick: async (record) => {
            showDialog({
              title: t("customFields.deleteConfirm", "Delete Custom Field?"),
              description: t(
                "customFields.deleteMessage",
                "Are you sure you want to delete this custom field?"
              ),
              type: "error",
              actions: [
                {
                  type: "primary",
                  title: t("common:error.noCancel", "No, Cancel"),
                },
                {
                  type: "error",
                  title: t("common:error.yesDelete", "Yes, Delete"),
                  onClick: async () => {
                    try {
                      await deleteMutationHandle.mutateAsync({
                        id: record._id,
                      });
                      hideDialog();
                      onRefresh();
                    } catch (error) {
                      console.error("Error deleting custom field:", error);
                      showDialog({
                        title: t("common:error.title", "Error"),
                        description: t(
                          "customFields.deleteError",
                          "An error occurred while deleting the custom field."
                        ),
                      });
                    }
                  },
                },
              ],
            });
          },
        },
      ];
    }, [
      hasWriteAccess,
      deleteMutationHandle,
      showDialog,
      hideDialog,
      t,
      onRefresh,
    ]);

  const fields: TableField<CustomFieldDefinitionListItem, AssetStatus>[] = [
    {
      value: (item) => item.context.map(customFieldContextLabel).join(", "),
      label: t("customFields.entities", "Entities"),
      type: "string",
    },
    {
      value: "label",
      label: t("customFields.self", "Label"),
      type: "string",
    },
    {
      value: "type",
      label: t("customFields.type", "Type"),
      type: "string",
    },
    {
      value: "isRequirement",
      label: t("customFields.requirement", "Requirement"),
      type: "boolean",
    },
    {
      value: (item) => {
        if (item.isExtensionField && item.extensionId) {
          const extensionName = extensionMap[item.extensionId] || "Extension";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ExtensionIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Chip
                label={extensionName}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {t("customFields.userDefined", "User Defined")}
          </Box>
        );
      },
      label: t("customFields.source", "Source"),
      type: "custom",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignSelf: "flex-end",
          mb: 2,
        }}
      >
        {/* <ExcelImporter
          mapping={customFieldDefinitionsMapping}
          schema={customFieldDefinitionSchema}
          onBulkCreate={onBulkCreate}
        /> */}
        <Button data-testid={testIds.customFieldsListCreateField}
          variant="contained"
          color="secondary"
          size="large"
          id="new-customFieldDefinition-button"
          onClick={() => {
            setFormOpen(true);
            setSelectedCustomFieldDefinition(null);
          }}
        >
          {t("customFields.buttonName", "New Custom Field")}
        </Button>
      </Box>
      <SmartLynksTable
        query={query}
        dataKey="cutomFieldDefinitions"
        fields={fields}
        actions={customActions}
        onRecordClick={(customFieldDefinition) => {
          if (!hasWriteAccess) return;
          // Prevent editing extension fields and show a message
          if (customFieldDefinition.isExtensionField) {
            enqueueSnackbar(
              t(
                "customFields.extensionFieldReadOnly",
                "Extension-provided custom fields are read-only and cannot be edited"
              ),
              { variant: "info" }
            );
            return;
          }
          setFormOpen(true);
          setSelectedCustomFieldDefinition(customFieldDefinition);
        }}
      />
      <Dialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {capitalize(
            selectedCustomFieldDefinition
              ? t("customFields.edit", "Edit Custom Field")
              : t("customFields.new", "New Custom Field")
          )}
        </DialogTitle>
        <DialogContent>
          {formOpen ? (
            <CustomFieldDefinitionFormContainer
              customFieldDefinitionId={selectedCustomFieldDefinition?._id}
              onSaved={() => {
                setFormOpen(false);
                onRefresh();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
